import { useEffect, useMemo, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import Button from './Button'
import FirstPage from './FirstPage'
import MeasurementInput from './MeasurementInput'
import PulseLoaderWithBackground from './PulseLoaderWithBackground'
import Results from './Results'
import Steps from './Steps'
import VideoJS from './VideoJS'
import arrowWhite from '../assets/icons/arrow-white.svg'
import arrow from '../assets/icons/arrow.svg'
import AUBandSizes from '../data/au/AU-bands.json'
import bandSizes from '../data/EUbandsizes.json'
import FRBandSizes from '../data/fr/FR-bands.json'
import ITBandSizes from '../data/it/IT-bands.json'
import UKBandSizes from '../data/uk/UK-bands.json'
import USBandSizes from '../data/us/US-bands.json'
import { posters, videos } from '../data/videos'
import {
  findBandSize,
  getChart,
  getRange,
  handleGetAlternativeSizes,
  handleGetSizes,
  scrollToTop,
  sendInputToGTM,
} from '../helpers'
import quizStyles from '../styles/CalculatorQuiz.module.css'
import '../styles/globals.css'

const CalculatorQuiz = () => {
  const [alternativeBandSize, setAlternativeBandSize] = useState('')
  const [showFirstPage, setShowFirstPage] = useState(true)
  const [step, setStep] = useState(0)
  const [quizFinished, setQuizFinished] = useState(false)
  const [chart, setChart] = useState('')
  const [paramChart, setParamChart] = useState('')
  const [inputBandMeasurement, setInputBandMeasurement] = useState('')
  const [inputBustMeasurement, setInputBustMeasurement] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [lang, setLang] = useState('')
  const [showSize, setShowSize] = useState(false)
  const [size, setSize] = useState('')
  const [cup, setCup] = useState('')
  const [smallerBand, setSmallerBand] = useState(false)
  const [smallerBust, setSmallerBust] = useState(false)
  const [largerBand, setLargerBand] = useState(false)
  const [largerBust, setLargerBust] = useState(false)
  const [isAlternativeSize, setIsAlternativeSize] = useState(false)
  const location = useLocation()
  const centimeterCharts = ['eu', 'au', 'fr', 'it']
  const inchesCharts = ['us', 'uk']

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search)
    setLang(queryParams.get('lang'))
    setParamChart(queryParams.get('chart'))
    setChart(getChart(queryParams.get('chart')))
  }, [location])

  const { t, i18n } = useTranslation()
  useEffect(() => {
    const setLanguage = () => {
      i18n.changeLanguage(lang)
    }
    setLanguage()
  }, [lang, i18n])

  const bandSize = useMemo(() => {
    let chartToUse = bandSizes
    if (chart === 'us') {
      chartToUse = USBandSizes
    } else if (chart === 'au') {
      chartToUse = AUBandSizes
    } else if (chart === 'fr') {
      chartToUse = FRBandSizes
    } else if (chart === 'it') {
      chartToUse = ITBandSizes
    } else if (chart === 'uk') {
      chartToUse = UKBandSizes
    }
    const returnSize = findBandSize(inputBandMeasurement, chartToUse)
    // only resets input if it's not in the range of the chart
    if (returnSize && !getRange(returnSize, chart).includes(inputBustMeasurement)) {
      setInputBustMeasurement('')
    }
    return returnSize
  }, [inputBandMeasurement, chart])

  useEffect(() => {
    setShowSize(false)
    setSize([])
  }, [inputBandMeasurement, inputBustMeasurement, chart])

  const handleShowSize = () => {
    let eventName = 'size_suggestion_fail'
    let isAlternative = false
    let sizes = []
    let cupValue = ''
    let alternativeBandSizeValue = ''

    const primarySizes = handleGetSizes(inputBustMeasurement, inputBandMeasurement, bandSize, chart, setCup)
    if (primarySizes.length && primarySizes[0] !== 'size unavailable') {
      sizes = primarySizes
      eventName = 'size_suggestion_success'
      // Extract cup and band from the sizes array (e.g., "32A" -> band: "32", cup: ["A"])
      if (sizes.length > 0) {
        // Extract band size from first size (assumes all sizes have same band)
        const firstSize = sizes[0]
        const bandMatch = firstSize.match(/^(\d+)/)
        const bandFromSize = bandMatch ? bandMatch[1] : ''

        // Extract all cups from sizes array - cup should be an array for linkToBras
        const cups = sizes.map((size) => size.replace(/^\d+/, ''))
        cupValue = cups.length > 0 ? cups : []
        alternativeBandSizeValue = bandFromSize
      }
    } else {
      const alternativeResult = handleGetAlternativeSizes(inputBustMeasurement, inputBandMeasurement, bandSize, chart)
      if (alternativeResult.isAvailable) {
        sizes = alternativeResult.sizes
        // Convert cup string to array for linkToBras function
        cupValue = alternativeResult.cup ? [alternativeResult.cup] : []
        alternativeBandSizeValue = alternativeResult.alternativeBandSize
        eventName = 'size_suggestion_success'
        isAlternative = true
      }
    }

    setSize(sizes)
    setCup(cupValue)
    setAlternativeBandSize(alternativeBandSizeValue)
    setIsAlternativeSize(isAlternative)
    setShowSize(true)
    sendInputToGTM(eventName, inputBustMeasurement, inputBandMeasurement, sizes, isAlternative)
  }

  const reset = () => {
    setShowFirstPage(true)
    setStep(0)
    setShowSize(false)
    setIsLoading(false)
    setSize('')
    setInputBandMeasurement('')
    setInputBustMeasurement('')
    setCup('')
    setIsAlternativeSize(false)
    setAlternativeBandSize('')
  }

  const nextStep = () => {
    if (step === 0) {
      setStep(step + 1)
    } else if (step === 1 && inputBandMeasurement) {
      setStep(step + 1)
    } else if (step === 2 && inputBustMeasurement) {
      setIsLoading(true)
      setStep(null)
      setQuizFinished(true)
      handleShowSize()
      setTimeout(() => {
        setIsLoading(false)
      }, 1000)
    }
    setTimeout(() => {
      scrollToTop()
    }, 100)
  }

  const goBack = () => {
    if (step > 0) {
      setStep(step - 1)
    }
  }

  const redoQuiz = () => {
    setStep(0)
    setQuizFinished(false)
    reset()
  }

  const playerRef = useRef(null)

  const activeVideoStep = step ?? 0

  const videoJsOptions = useMemo(
    () => ({
      autoplay: true,
      preload: 'auto',
      controls: false,
      muted: true,
      responsive: true,
      aspectRatio: '1:1',
      loop: true,
      poster: posters[activeVideoStep],
      playsinline: true,
      disablePictureInPicture: true,
      controlBar: {
        fullscreenControl: false,
      },
      sources: [
        {
          src: videos[activeVideoStep],
          type: 'application/x-mpegURL',
        },
      ],
    }),
    [activeVideoStep]
  )

  const handlePlayerReady = (player) => {
    playerRef.current = player
  }

  return (
    <div className={quizStyles['beige-container']}>
      {showFirstPage && <FirstPage setShowFirstPage={setShowFirstPage} />}
      {!showFirstPage && isLoading && (
        <div className={quizStyles['loading-wrapper']}>
          <PulseLoaderWithBackground isLoading={isLoading} />
        </div>
      )}
      <div
        className={`${quizStyles['inner-wrapper']} ${!quizFinished && quizStyles['grid-rows-3']}`}
        style={!showFirstPage && !isLoading ? { display: 'grid' } : { display: 'none' }}>
        {!quizFinished && <Steps step={step + 1} setStep={setStep} inputBandMeasurement={inputBandMeasurement} />}
        {!quizFinished && (
          <div className={quizStyles['quiz-container']}>
            <div className={quizStyles['instructions-container']}>
              <h2>
                {step === 0 && t('stepone_title')}
                {step === 1 && t('band_measurement')}
                {step === 2 && t('bust_measurement')}
              </h2>
              {step !== null && <VideoJS options={videoJsOptions} onReady={handlePlayerReady} />}
              <p>
                {step === 0 && t('stepone_instructions')}
                {step === 1 && t('band_instructions')}
                {step === 2 && t('bust_instructions')}
              </p>
            </div>
            {step === 1 && centimeterCharts.includes(chart) && (
              <MeasurementInput
                inputMeasurement={inputBandMeasurement}
                onChangeFunction={setInputBandMeasurement}
                inchesCharts={inchesCharts}
                chart={chart}
                setSmaller={setSmallerBand}
                setLarger={setLargerBand}
                bandSizes={bandSizes}
              />
            )}
            {step === 1 && inchesCharts.includes(chart) && (
              <MeasurementInput
                inputMeasurement={inputBandMeasurement}
                onChangeFunction={setInputBandMeasurement}
                inchesCharts={inchesCharts}
                chart={chart}
                setSmaller={setSmallerBand}
                setLarger={setLargerBand}
                bandSizes={UKBandSizes}
              />
            )}
            {step === 2 && (
              <MeasurementInput
                inputMeasurement={inputBustMeasurement}
                onChangeFunction={setInputBustMeasurement}
                inchesCharts={inchesCharts}
                chart={chart}
                setSmaller={setSmallerBust}
                setLarger={setLargerBust}
                bandSizes={UKBandSizes}
                bandSize={bandSize}
                getRange={getRange}
              />
            )}
          </div>
        )}
        {quizFinished ? (
          <div className={quizStyles['quiz-container']}>
            {showSize && (
              <Results
                size={size}
                chart={chart}
                lang={lang}
                paramChart={paramChart}
                smallerBand={smallerBand}
                smallerBust={smallerBust}
                largerBand={largerBand}
                largerBust={largerBust}
                bandSize={bandSize}
                redoQuiz={redoQuiz}
                cup={cup}
                alternativeBandSize={alternativeBandSize}
                isAlternativeSize={isAlternativeSize}
              />
            )}
          </div>
        ) : (
          <div className={quizStyles['controllers-wrapper']}>
            {step === 0 && (
              <Button
                onClick={() => setShowFirstPage(true)}
                variant="outlined"
                text={t('back')}
                image={arrow}
                arrow="left"></Button>
            )}
            {step > 0 && (
              <Button onClick={goBack} variant="outlined" text={t('back')} image={arrow} arrow="left"></Button>
            )}
            <Button
              onClick={nextStep}
              variant="black"
              text={step === 2 ? t('get_results') : t('next')}
              image={arrowWhite}
              disabled={(step === 1 && !inputBandMeasurement) || (step === 2 && !inputBustMeasurement)}
              firstStep={step === 0}></Button>
          </div>
        )}
      </div>
    </div>
  )
}

export default CalculatorQuiz
